import React from 'react';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import { IconDefinition } from '@fortawesome/pro-solid-svg-icons';
import { twMerge } from 'tailwind-merge';
import { Tooltip } from '../Tooltip';
import { CircularProgress } from '../CircularProgress';

/**
 * Props for the PulseIconButton component
 */
export interface PulseIconButtonProps {
  /**
   * The style variant of the button
   * @default 'solid'
   */
  variant?: 'solid' | 'plain' | 'soft' | 'outline';

  /**
   * The color variant of the button
   * @default 'primary'
   */
  color?: 'primary' | 'secondary' | 'neutral' | 'danger' | 'success';

  /**
   * The size of the button
   * @default 'md'
   */
  size?: 'sm' | 'md' | 'lg';

  /**
   * Whether the button is disabled
   * @default false
   */
  disabled?: boolean;

  /**
   * FontAwesome icon to display in the button
   */
  icon: IconDefinition;

  /**
   * Click handler for the button
   */
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;

  /**
   * HTML button type
   * @default 'button'
   */
  type?: 'button' | 'submit' | 'reset';

  /**
   * Additional CSS classes
   */
  className?: string;

  /**
   * Accessible label for the button (required for screen readers)
   */
  'aria-label': string;

  /**
   * Tooltip content to display on hover
   * A tooltip will appear after a brief delay
   */
  tooltip?: string | React.ReactNode;

  /**
   * Position of the tooltip relative to the button
   * @default 'top'
   */
  tooltipPosition?: 'top' | 'bottom' | 'left' | 'right';

  /**
   * Whether the button is in a loading state
   * @default false
   */
  loading?: boolean;

  /**
   * Test ID for testing and analytics
   */
  'data-testid'?: string;

  /**
   * Ref to the underlying button, e.g. to return focus to it
   */
  ref?: React.Ref<HTMLButtonElement>;

  /**
   * Whether the region this button controls is expanded. Set it when the button is a
   * disclosure toggle so screen readers announce the open/closed state.
   */
  'aria-expanded'?: boolean;
}

/**
 * PulseIconButton - An icon-only button component with multiple variants and sizes
 *
 * @example
 * ```tsx
 * import { faTrash } from '@fortawesome/pro-solid-svg-icons';
 *
 * <PulseIconButton
 *   icon={faTrash}
 *   color="danger"
 *   size="md"
 *   aria-label="Delete item"
 *   tooltip="Delete item"
 *   onClick={handleDelete}
 * />
 * ```
 *
 * @example
 * ```tsx
 * import { faEdit } from '@fortawesome/pro-solid-svg-icons';
 *
 * <PulseIconButton
 *   icon={faEdit}
 *   variant="soft"
 *   color="primary"
 *   size="lg"
 *   aria-label="Edit profile"
 *   tooltip="Edit your profile"
 *   tooltipPosition="bottom"
 *   disabled
 * />
 * ```
 */
const PulseIconButton: React.FC<PulseIconButtonProps> = ({
  variant = 'solid',
  color = 'primary',
  size = 'md',
  disabled = false,
  icon,
  onClick,
  type = 'button',
  className = '',
  'aria-label': ariaLabel,
  tooltip,
  tooltipPosition = 'top',
  loading = false,
  'data-testid': dataTestId,
  ref,
  'aria-expanded': ariaExpanded,
}): React.ReactElement => {
  /**
   * Get background color classes based on variant, color, and state
   */
  const getBackgroundClasses = (): string => {
    if (variant === 'solid') {
      const solidColorMap = {
        primary: {
          default: 'bg-sky-600',
          hover: 'hover:bg-sky-800',
          disabled: 'bg-sky-50',
        },
        secondary: {
          default: 'bg-teal-600',
          hover: 'hover:bg-teal-700',
          disabled: 'bg-teal-50',
        },
        neutral: {
          default: 'bg-neutral-500',
          hover: 'hover:bg-neutral-600',
          disabled: 'bg-neutral-100',
        },
        danger: {
          default: 'bg-red-500',
          hover: 'hover:bg-red-600',
          disabled: 'bg-red-50',
        },
        success: {
          default: 'bg-green-700',
          hover: 'hover:bg-green-800',
          disabled: 'bg-green-50',
        },
      };

      const colors = solidColorMap[color];
      return disabled ? colors.disabled : `${colors.default} ${colors.hover}`;
    }

    if (variant === 'soft') {
      // Soft variant - disabled state
      if (disabled) {
        return 'bg-neutral-50';
      }

      const softColorMap = {
        primary: {
          default: 'bg-sky-50',
          hover: 'hover:bg-sky-100',
        },
        secondary: {
          default: 'bg-teal-50',
          hover: 'hover:bg-teal-100',
        },
        neutral: {
          default: 'bg-neutral-100',
          hover: 'hover:bg-neutral-200',
        },
        danger: {
          default: 'bg-red-100',
          hover: 'hover:bg-red-200',
        },
        success: {
          default: 'bg-green-100',
          hover: 'hover:bg-green-200',
        },
      };

      const colors = softColorMap[color];
      return `${colors.default} ${colors.hover}`;
    }

    if (variant === 'outline') {
      // Outline variant - disabled state
      if (disabled) {
        return 'bg-transparent';
      }

      const outlineColorMap = {
        primary: 'hover:bg-sky-50',
        secondary: 'hover:bg-teal-50',
        neutral: 'hover:bg-neutral-50',
        danger: 'hover:bg-red-50',
        success: 'hover:bg-green-50',
      };

      return `bg-transparent ${outlineColorMap[color]}`;
    }

    // Plain variant
    if (disabled) {
      return 'bg-transparent';
    }

    const plainColorMap = {
      primary: 'hover:bg-sky-50',
      secondary: 'hover:bg-teal-50',
      neutral: 'hover:bg-neutral-200',
      danger: 'hover:bg-red-50',
      success: 'hover:bg-green-50',
    };

    return `bg-transparent ${plainColorMap[color]}`;
  };

  /**
   * Get text color classes based on variant, color, and state
   */
  const getTextClasses = (): string => {
    if (variant === 'solid') {
      if (!disabled) {
        return 'text-white';
      }

      const solidDisabledTextMap = {
        primary: 'text-sky-100',
        secondary: 'text-teal-100',
        neutral: 'text-neutral-300',
        danger: 'text-red-200',
        success: 'text-green-200',
      };

      return solidDisabledTextMap[color];
    }

    if (variant === 'soft') {
      // Soft variant - disabled state
      if (disabled) {
        return 'text-neutral-300';
      }

      const softTextMap = {
        primary: 'text-sky-900',
        secondary: 'text-teal-700',
        neutral: 'text-primary',
        danger: 'text-red-500',
        success: 'text-green-700',
      };

      return softTextMap[color];
    }

    if (variant === 'outline') {
      // Outline variant - disabled state
      if (disabled) {
        const outlineDisabledTextMap = {
          primary: 'text-sky-100',
          secondary: 'text-teal-100',
          neutral: 'text-neutral-200',
          danger: 'text-red-50',
          success: 'text-green-200',
        };

        return outlineDisabledTextMap[color];
      }

      const outlineTextMap = {
        primary: 'text-sky-900',
        secondary: 'text-teal-500',
        neutral: 'text-primary',
        danger: 'text-red-500',
        success: 'text-green-700',
      };

      return outlineTextMap[color];
    }

    // Plain variant
    if (disabled) {
      const plainDisabledTextMap = {
        primary: 'text-sky-100',
        secondary: 'text-teal-100',
        neutral: 'text-neutral-300',
        danger: 'text-red-200',
        success: 'text-green-200',
      };

      return plainDisabledTextMap[color];
    }

    const plainTextMap = {
      primary: 'text-sky-900',
      secondary: 'text-teal-700',
      neutral: 'text-primary',
      danger: 'text-red-500',
      success: 'text-green-700',
    };

    return plainTextMap[color];
  };

  /**
   * Get size classes for padding and icon size
   */
  const getSizeClasses = (): string => {
    const sizeMap = {
      sm: 'h-8 w-8 text-xs',
      md: 'h-10 w-10 text-sm',
      lg: 'h-12 w-12 text-base',
    };

    return sizeMap[size];
  };

  /**
   * Get border classes for outline variant
   */
  const getBorderClasses = (): string => {
    if (variant !== 'outline') {
      return '';
    }

    if (disabled) {
      const outlineDisabledBorderMap = {
        primary: 'border border-sky-100',
        secondary: 'border border-teal-100',
        neutral: 'border border-neutral-200',
        danger: 'border border-red-50',
        success: 'border border-green-200',
      };

      return outlineDisabledBorderMap[color];
    }

    const outlineBorderMap = {
      primary: 'border border-sky-600',
      secondary: 'border border-teal-500',
      neutral: 'border border-neutral-300',
      danger: 'border border-red-500',
      success: 'border border-green-700',
    };

    return outlineBorderMap[color];
  };

  const baseClasses =
    'inline-flex items-center justify-center rounded-full transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2';
  const backgroundClasses = getBackgroundClasses();
  const textClasses = getTextClasses();
  const sizeClasses = getSizeClasses();
  const borderClasses = getBorderClasses();
  const isDisabled = disabled || loading;
  const disabledClasses = isDisabled ? 'cursor-not-allowed' : 'cursor-pointer';
  const focusRingClass = isDisabled ? '' : 'focus:ring-sky-500';

  /**
   * Check if icon is a spinner to add animation
   */
  const isSpinnerIcon = (iconDef: IconDefinition): boolean => {
    return iconDef.iconName === 'spinner';
  };

  return (
    <Tooltip
      content={tooltip}
      position={tooltipPosition}
      delay={500}
      slotProps={{
        containerClassName: 'w-max max-w-xs',
      }}
    >
      <button
        ref={ref}
        type={type}
        disabled={isDisabled}
        onClick={onClick}
        aria-label={ariaLabel}
        aria-expanded={ariaExpanded}
        data-testid={dataTestId}
        className={twMerge(
          baseClasses,
          backgroundClasses,
          textClasses,
          sizeClasses,
          borderClasses,
          disabledClasses,
          focusRingClass,
          className
        )}
      >
        {loading ? (
          <CircularProgress size="sm" />
        ) : (
          <Icon
            icon={icon}
            className={isSpinnerIcon(icon) ? 'animate-spin' : ''}
          />
        )}
      </button>
    </Tooltip>
  );
};

export default PulseIconButton;
